<script setup>
import { onBeforeUnmount, ref } from 'vue'
import supplierHttp from '@supplier/http/supplierHttp'
import { useI18n } from 'vue-i18n'

import { SUPPLIER_API } from '@supplier/http/api-routes'

import { getValidationErrors, resolveErrorMessage } from '@supplier/utils/errors'
import { isValidStoreUrl, trimShopifyUrl } from '@supplier/utils/shopifyUrl'

import AppButton from '@ui/app-button/AppButton.vue'
import AppInput from '@ui/app-input/AppInput.vue'
import AppModal from '@ui/app-modal/AppModal.vue'

import EventBusV3 from '@/shared/eventBus'
import { SUPPLIER_CONNECT_STORE_MODAL_OPEN } from '@/shared/eventContract'

const FORM_ID = 'supplier-connect-store-form'

const { t } = useI18n()

const isOpen = ref(false)
const storeUrl = ref('')
const formError = ref('')
const isSubmitting = ref(false)

function open() {
  storeUrl.value = ''
  formError.value = ''
  isSubmitting.value = false
  isOpen.value = true
}

function normalize() {
  storeUrl.value = trimShopifyUrl(storeUrl.value)
}

function clearError() {
  formError.value = ''
}

function handlePaste(event) {
  const text = event.clipboardData?.getData('text') ?? ''
  if (!text) return
  event.preventDefault()
  storeUrl.value = trimShopifyUrl(text)
  clearError()
}

async function submit() {
  if (isSubmitting.value) return
  normalize()
  const url = storeUrl.value
  if (!url) {
    formError.value = t('ui.supplier_portal.connect_store.required')
    return
  }
  if (!isValidStoreUrl(url)) {
    formError.value = t('ui.supplier_portal.connect_store.invalid_url')
    return
  }
  isSubmitting.value = true
  try {
    await supplierHttp.post(SUPPLIER_API.validateStoreUrl, { store_url: url })
    window.location.assign(`/shopify?store_url=${encodeURIComponent(url)}`)
  } catch (error) {
    formError.value = getValidationErrors(error).store_url || resolveErrorMessage(error, t('ui.supplier_portal.common.generic_error'))
    isSubmitting.value = false
  }
}

EventBusV3.on(SUPPLIER_CONNECT_STORE_MODAL_OPEN, open)

onBeforeUnmount(() => {
  EventBusV3.off(SUPPLIER_CONNECT_STORE_MODAL_OPEN, open)
})
</script>

<template>
  <app-modal
    v-model="isOpen"
    :title="t('ui.supplier_portal.connect_store.title')"
    :max-width="480"
    data-element="supplier-connect-store-modal"
  >
    <template #body>
      <form
        :id="FORM_ID"
        class="supplier-connect-store-modal-form"
        novalidate
        @submit.prevent="submit"
      >
        <i18n-t
          keypath="ui.supplier_portal.connect_store.description"
          tag="p"
          class="supplier-connect-store-modal-text font-medium-body"
        >
          <template #store_url>
            <b>{{ t('ui.supplier_portal.connect_store.description_store_url') }}</b>
          </template>
        </i18n-t>
        <app-input
          v-model="storeUrl"
          name="store_url"
          autocomplete="off"
          block
          :placeholder="t('ui.supplier_portal.connect_store.placeholder')"
          :aria-label="t('ui.supplier_portal.connect_store.description_store_url')"
          :error="Boolean(formError)"
          :description="formError"
          data-element="supplier-connect-store-url-input"
          @input="clearError"
          @focusout="normalize"
          @paste="handlePaste"
        />
      </form>
    </template>

    <template #footer>
      <app-button
        type="submit"
        :form="FORM_ID"
        variant="accent"
        block
        :loading="isSubmitting"
        data-element="supplier-connect-store-submit-btn"
      >
        {{ t('ui.supplier_portal.connect_store.submit') }}
      </app-button>
    </template>
  </app-modal>
</template>

<style lang="scss" scoped>
.supplier-connect-store-modal {
  &-form {
    display: flex;
    flex-direction: column;
    gap: 16px;
  }

  &-text {
    color: var(--g-neutral-2);
  }
}
</style>
